import React, { Children, isValidElement, useState } from 'react';
import NextLink from 'next/link';

import { Button } from '@playbooks/ui/components/button';
import { Icon } from '@playbooks/ui/components/icon';
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@playbooks/ui/components/table';
import { cn } from '@playbooks/ui/lib/utils';

type MdxComponentMap = Record<string, React.ComponentType<any>>;
const Alert = ({ className, ...props }: React.ComponentProps<'aside'>) => {
	// Theme
	const rootClassName = cn('my-6 flex gap-3 rounded-media border bg-surface p-4', className);

	// Render
	return <aside className={rootClassName} {...props} />;
};
const AlertIcon = ({ className, ...props }: React.ComponentProps<'span'>) => {
	// Theme
	const rootClassName = cn('mt-0.5 shrink-0 text-foreground-body', className);

	// Render
	return <span className={rootClassName} {...props} />;
};
const AlertBody = ({ className, ...props }: React.ComponentProps<'div'>) => {
	// Theme
	const rootClassName = cn('min-w-0 flex-1', className);

	// Render
	return <div className={rootClassName} {...props} />;
};
const AlertText = ({ className, ...props }: React.ComponentProps<'p'>) => {
	// Theme
	const rootClassName = cn('text-sm text-foreground-body', className);

	// Render
	return <p className={rootClassName} {...props} />;
};
const DocsAlertText = ({ children, ...props }: React.ComponentProps<typeof AlertText>) => {
	const nodes = Children.toArray(children);
	const content =
		nodes.length === 1 && isValidElement(nodes[0]) && nodes[0].type === 'p'
			? (
					nodes[0].props as {
						children?: React.ReactNode;
					}
				).children
			: children;
	return (
		<p {...props} className='text-sm text-foreground-body'>
			{content}
		</p>
	);
};
const getTextContent = (node: React.ReactNode): string => {
	if (typeof node === 'string' || typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(getTextContent).join('');
	if (React.isValidElement(node))
		return getTextContent(
			(
				node.props as {
					children?: React.ReactNode;
				}
			).children,
		);
	return '';
};
const createHeading = (level: 1 | 2 | 3 | 4 | 5 | 6) => {
	const Heading = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	return function DocsHeading({ id, className, children, ...props }: React.ComponentProps<'h2'>) {
		// Theme
		const rootClassName = cn('group scroll-mt-24', className);

		// Render
		return (
			<Heading id={id} className={rootClassName} {...props}>
				<a href={id ? `#${id}` : undefined} className='docs-heading-anchor'>
					{children}
					{id && (
						<span aria-hidden className='docs-heading-hash'>
							#
						</span>
					)}
				</a>
			</Heading>
		);
	};
};
const DocsLink = ({ href = '', className, children, ...props }: React.ComponentProps<'a'>) => {
	// Theme
	const rootClassName = cn('docs-inline-link', className);

	// Render
	if (href.startsWith('/') && !href.startsWith('//')) {
		return (
			<NextLink href={href} className={rootClassName} {...props}>
				{children}
			</NextLink>
		);
	}
	return (
		<a href={href} className={rootClassName} {...props}>
			{children}
		</a>
	);
};
const DocsPre = ({
	filename,
	copy,
	className,
	children,
	...props
}: React.ComponentProps<'pre'> & {
	filename?: string;
	copy?: boolean;
}) => {
	const [copied, setCopied] = useState(false);
	const onCopy = async () => {
		await window.navigator.clipboard.writeText(getTextContent(children));
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1500);
	};
	return (
		<div className='docs-code-block'>
			{(filename || copy) && (
				<div className='docs-code-header'>
					<span>{filename || 'Code'}</span>
					{copy && (
						<Button variant='ghost' size='icon-xs' aria-label={copied ? 'Copied' : 'Copy code'} onClick={onCopy}>
							<Icon type='far' icon={copied ? 'check' : 'copy'} aria-hidden />
						</Button>
					)}
				</div>
			)}
			<pre className={className} {...props}>
				{children}
			</pre>
		</div>
	);
};
const DocsImage = ({ alt = '', className, ...props }: React.ComponentProps<'img'>) => {
	// Theme
	const rootClassName = cn('docs-image', className);

	// Render
	return (
		<figure>
			<img alt={alt} className={rootClassName} {...props} />
			{alt && <figcaption>{alt}</figcaption>}
		</figure>
	);
};
export const docsMdxComponents: MdxComponentMap = {
	a: DocsLink,
	blockquote: props => <blockquote {...props} />,
	code: props => <code {...props} />,
	h1: createHeading(1),
	h2: createHeading(2),
	h3: createHeading(3),
	h4: createHeading(4),
	h5: createHeading(5),
	h6: createHeading(6),
	img: DocsImage,
	pre: DocsPre,
	table: props => <Table {...props} />,
	thead: props => <TableHeader {...props} />,
	tbody: props => <TableBody {...props} />,
	tr: props => <TableRow {...props} />,
	th: props => <TableHead {...props} />,
	td: props => <TableCell {...props} />,
	caption: props => <TableCaption {...props} />,
	Alert,
	AlertIcon,
	AlertBody,
	AlertText: DocsAlertText,
};
